import React, { useEffect, useState } from 'react';
import { 
  Card, CardContent, CardHeader, Grid, Typography, Box, CircularProgress, 
  Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, IconButton, Tooltip,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';
import { publicInstance } from '../axiosInstance';
import GrafanaIcon from '../../Components/Assets/Grafana_icon.svg';

interface ConnectionStatsProps {
  deviceNumber: string;
}

const getDotColor = (percentage: number): string => {
  if (percentage > 83.3) return 'green';
  if (percentage > 47.6) return 'yellow';
  return 'red';
};

const ConnectionStats: React.FC<ConnectionStatsProps> = ({ deviceNumber }) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [timeRange, setTimeRange] = useState<string>('7d');
  const theme = useTheme();

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const p_end_ts = new Date();
        const p_start_ts = new Date();
        let days = 7;

        if (timeRange === '1d') {
          days = 1;
        } else if (timeRange === '7d') {
          days = 7;
        } else if (timeRange === '30d') {
          days = 30;
        }
        
        p_start_ts.setDate(p_end_ts.getDate() - days);

        const body = {
          p_box: parseInt(deviceNumber, 10),
          p_start_ts: p_start_ts.toISOString(),
          p_end_ts: p_end_ts.toISOString(),
        };

        const response = await publicInstance.post('/rpc/get_connection_sums', body);
        setStats(response.data ? response.data[0] : {});
        setLoading(false);
      } catch (error) {
        const err = error as any;
        setError(`Error fetching connection stats: ${err.message}`);
        setLoading(false);
      }
    };

    fetchStats();
  }, [deviceNumber, timeRange]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    setTimeRange(event.target.value as string);
  };

  const statsKeys = ['sum_vpna', 'sum_vpns', 'sum_vpnb', 'sum_ping'];
  const labels = ['VPN A', 'VPN S', 'VPN B', 'Ping'];

  const validStats = stats && typeof stats === 'object';

  const max = timeRange === '30d' ? 180 : timeRange === '7d' ? 42 : 6;

  return (
    <Card sx={{ mb: 2, border: `1px solid ${theme.palette.borders.primary}` }}>
      <CardHeader
        title="Connection Stats"
        action={
          <Box display="flex" alignItems="center">
            <FormControl variant="outlined" size="small" sx={{ mr: 6 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                onChange={handleChange}
                label="Time Range"
              >
                <MenuItem value="1d">Last 1 Day</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
              </Select>
            </FormControl>
            
          </Box>
        }
      />
      <CardContent>
        {loading ? (
          <Grid container spacing={2}>
            {statsKeys.map((_, index) => (
              <Grid item xs={3} key={index}>
                <Box display="flex" justifyContent="center" alignItems="center" height={80}>
                  <CircularProgress />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {statsKeys.map((key, index) => {
              const value = validStats ? stats[key] : 0;
              const percentage = ((value / max) * 100).toFixed(1);

              return (
                <Grid item xs={3} key={index}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      {labels[index]}
                    </Typography>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: getDotColor(parseFloat(percentage)),
                        ml: 1,
                      }}
                    />
                  </Box>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {`${percentage}%`}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        )}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </CardContent>
    </Card>
  );
};

export default ConnectionStats;
